import {Badge, DataStatus, openDialog} from 'platform/components';
import {Show, Text} from 'platform/foundation';
import {match} from 'ts-pattern';

import {useState} from 'react';

import {isNil, isNotNil} from 'ramda';

import {useLazyGetFileByIdQuery} from '@dms/api/base';
import {useGetLatestCarPassRequestByVehicleQuery} from '@dms/api/vehicle';
import i18n from '@dms/i18n';
import {downloadHostedFile, handleApiError, Widget} from '@dms/shared';

import {RequiredTestIdProps, suffixTestId, useBoolean} from 'shared/web';

import {CarPassActionRequiredDialog} from './components/CarPassActionRequiredDialog';
import {CarPassBody} from './components/CarPassBody';
import {CarPassLogo} from './components/CarPassLogo';
import {CarPassSubmitDialog} from './components/CarPassSubmitDialog';
import {CarPassState, deriveCarPassState} from './utils/deriveCarPassState';
import {getCarPassWidgetActions} from './utils/getCarPassWidgetActions';
import {getPollingInterval} from './utils/getPollingInterval';
import {getStateBadge} from './utils/getStateBadge';

const CAR_PASS_DEFAULT_FILENAME = 'car-pass.pdf';

export interface CarPassWidgetProps extends RequiredTestIdProps {
  vehicleId: string;
}

export function CarPassWidget(props: CarPassWidgetProps) {
  const [pollingInterval, setPollingInterval] = useState(0);

  const {data: request, isLoading: isWidgetLoading} = useGetLatestCarPassRequestByVehicleQuery(
    {vehicleId: props.vehicleId},
    {pollingInterval}
  );

  const [getCarPassFile] = useLazyGetFileByIdQuery();
  const [isDownloadingDocument, startDownloading, finishDownloading] = useBoolean(false);

  const uiState = deriveCarPassState(request);
  const derivedPollingInterval = getPollingInterval(uiState);
  if (pollingInterval !== derivedPollingInterval) {
    setPollingInterval(derivedPollingInterval);
  }

  const badge = getStateBadge(uiState);

  const openSubmitDialog = () => {
    openDialog(
      <CarPassSubmitDialog
        vehicleId={props.vehicleId}
        data-testid={suffixTestId('submitDialog', props)}
      />,
      {
        title: i18n.t('page.carPass.labels.submitDialogTitle'),
        scrollBehavior: 'outside',
        'data-testid': suffixTestId('submitDialogContainer', props),
      }
    );
  };

  const openActionRequiredDialog = () => {
    if (isNil(request)) {
      return;
    }
    openDialog(
      <CarPassActionRequiredDialog
        vehicleId={props.vehicleId}
        requestId={request.id}
        issues={request.issues}
        data-testid={suffixTestId('actionRequiredDialog', props)}
      />,
      {
        title: i18n.t('page.carPass.labels.actionRequiredDialogTitle'),
        scrollBehavior: 'outside',
        'data-testid': suffixTestId('actionRequiredDialogContainer', props),
      }
    );
  };

  const openDocumentInNewTab = () => {
    if (isNotNil(request?.documentPublicUrl)) {
      window.open(request.documentPublicUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const downloadDocument = () => {
    const fileId = request?.documentId;
    if (isNil(fileId)) {
      return;
    }

    startDownloading();
    getCarPassFile({fileId})
      .unwrap()
      .then((file) =>
        downloadHostedFile(file.originalUri, file.originalFilename ?? CAR_PASS_DEFAULT_FILENAME)
      )
      .catch(handleApiError)
      .finally(finishDownloading);
  };

  const subtitle = i18n.t(getSubtitleKey(uiState));

  const actions = getCarPassWidgetActions({
    uiState,
    hasDocument: isNotNil(request?.documentPublicUrl),
    hasDownloadableDocument: isNotNil(request?.documentId),
    isDownloadingDocument,
    onRequest: openSubmitDialog,
    onResolve: openActionRequiredDialog,
    onView: openDocumentInNewTab,
    onDownload: downloadDocument,
    onRenew: openSubmitDialog,
    onRetry: openSubmitDialog,
    testId: props['data-testid'],
  });

  return (
    <Widget.Card data-testid={suffixTestId('carPassWidget', props)}>
      <DataStatus isLoading={isWidgetLoading}>
        <Widget.Header
          logo={<CarPassLogo state={uiState} />}
          title={i18n.t('page.carPass.labels.widgetTitle')}
          headerActions={
            <Badge variant="subtle" colorScheme={badge.colorScheme} value={badge.value} />
          }
          subtitle={
            <Text size="xSmall" color="secondary">
              {subtitle}
            </Text>
          }
          actions={actions}
          data-testid={suffixTestId('carPassWidget', props)}
        />
        <Show when={uiState !== CarPassState.NONE ? uiState : null}>
          {(uiState) => (
            <Widget.Content data-testid={suffixTestId('carPassWidget-body', props)}>
              <CarPassBody
                uiState={uiState}
                request={request}
                data-testid={suffixTestId('carPassWidget', props)}
              />
            </Widget.Content>
          )}
        </Show>
      </DataStatus>
    </Widget.Card>
  );
}

function getSubtitleKey(uiState: CarPassState): string {
  return match(uiState)
    .with(CarPassState.NONE, () => 'page.carPass.labels.noneDescription')
    .with(CarPassState.PENDING, () => 'page.carPass.labels.pendingDescription')
    .with(CarPassState.WAITING_FOR_DIV, () => 'page.carPass.labels.waitingForDivDescription')
    .with(CarPassState.ACTION_TAKEN, () => 'page.carPass.labels.actionTakenDescription')
    .with(CarPassState.ACTION_REQUIRED, () => 'page.carPass.labels.actionRequiredDescription')
    .with(CarPassState.READY, () => 'page.carPass.labels.readyDescription')
    .with(CarPassState.EXPIRED, () => 'page.carPass.labels.expiredDescription')
    .with(CarPassState.FAILED, () => 'page.carPass.labels.errorDescription')
    .exhaustive();
}
